import { useState, useEffect } from 'react';
import Axios from 'axios';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';

// Utils
import {
    getDataWithComp,
    getAlert,
    deleteData,
} from '../../../../utils/SamuiUtils';
import moment from 'moment';
import Pagination from '../../../components/Footer/Pagination';
import CarQueueEditor from './CarQueueEditor';

function Form({ name }) {
    const [mode, setMode] = useState('S');
    const [dlLineId, setDlLineId] = useState(null);
    const [dlLineName, setDlLineName] = useState(null);
    const [dlLineList, setDlLineList] = useState([]);
    const [dlLineDetail, setDlLineDetail] = useState([]);
    const [carDataList, setCarDataList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    // ค้นหาลูกค้า
    const [searchCarNoOrName, setSearchCarNoOrName] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [newLineName, setNewLineName] = useState('');

    // สำหรับ Freeze หน้าจอตอนทำรายการ
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        initialize();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.classList.contains('modal')) {
                setShowModal(false);
            }
        };
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    const initialize = async () => {
        try {
            setIsLoading(true);
            setMode('S');
            setDlLineList([]);
            setIsEditing(false);

            // ค้นหาข้อมูลจาก DL_Line_H
            const dlLineList = await getDataWithComp('DL_Line_H', `ORDER BY DL_Id ASC`);
            if (dlLineList && dlLineList.length > 0) {
                setDlLineList(dlLineList);
            }

            // ค้นหาข้อมูลรถ
            const carDataList = await getDataWithComp(
                "Tb_Set_Car",
                "AND Car_Status = 'Y' ORDER BY Car_No ASC"
            );
            if (carDataList && carDataList.length > 0) {
                setCarDataList(carDataList);
            }
        } catch (error) {
            setIsLoading(false);
            getAlert('FAILED', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const response = await Axios.post(
                `${process.env.REACT_APP_API_URL}/api/create-dl-line-h`,
                {
                    dl_line_name: newLineName,
                    dl_remark: '',
                    created_date: moment(),
                    created_by: localStorage.getItem('name'),
                    comp_id: localStorage.getItem('company'),
                },
                {
                    headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
                }
            );

            if (response.data.status === "OK") {
                setNewLineName('');
                setShowModal(false);
                getAlert(response.data.status, response.data.message);
                initialize();
            }

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            getAlert('FAILED', error.message);
        }
    };

    const handleUpdate = async () => {
        try {
            setIsLoading(true);

            const response = await Axios.post(
                `${process.env.REACT_APP_API_URL}/api/update-dl-line-h`,
                {
                    dl_id: dlLineId,
                    dl_line_name: newLineName,
                    dl_remark: '',
                    created_date: moment(),
                    created_by: localStorage.getItem('name'),
                    comp_id: localStorage.getItem('company'),
                },
                {
                    headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
                }
            );

            if (response.data.status === "OK") {
                setDlLineId('');
                setNewLineName('');
                setShowModal(false);
                getAlert(response.data.status, response.data.message);
                initialize();
            }

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            getAlert('FAILED', error.message);
        }
    };

    // สำหรับลบ (อันนี้อันตราย ยังไม่ให้ลูกค้าใช้)
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const handleDelete = async () => {
        try {
            await deleteData('DL_Line_H', `WHERE DL_Id = ${dlLineId}`);
            setShowConfirmModal(false);
            getAlert('OK', 'ลบข้อมูลสําเร็จ');
            initialize();
        } catch (error) {
            getAlert('FAILED', error.message);
        }
    };

    const confirmAndSubmit = async () => {
        try {
            getAlert('C_DIALOG', 'ยืนยันการบันทึกข้อมูล', async (isConfirm) => {
                if (isConfirm) {
                    await handleSubmit();
                }
            });
        } catch (error) {
            console.error(error);
        }
    };

    const confirmAndUpdate = async () => {
        try {
            getAlert('C_DIALOG', 'ยืนยันการแก้ไขข้อมูล', async (isConfirm) => {
                if (isConfirm) {
                    await handleUpdate();
                }
            });
        } catch (error) {
            console.error(error);
        }
    };

    const onRowSelected = async (dlId) => {
        try {
            setDlLineDetail([]);
            setCurrentPage(1);
            setSearchCarNoOrName('');
            setIsEditing(false);

            // ค้นหาข้อมูลจาก DL_Line_H
            const dlLineDetail = await getDataWithComp('View_DL_Car', `AND DL_Id = ${dlId} ORDER BY DL_Queue ASC`);
            const validDetail = dlLineDetail.filter(item => item.DL_Car_id != null);
            if (validDetail.length > 0) {
                setDlLineDetail(validDetail);
            }
        } catch (error) {
            getAlert('FAILED', error.message);
        }
    }

    // ใช้กรอง และค้นหา
    let filteredDetail = dlLineDetail;
    if (searchCarNoOrName.trim() !== '') {
        filteredDetail = filteredDetail.filter(d =>
            (d.Car_No + '').toLowerCase().includes(searchCarNoOrName.trim().toLowerCase()) ||
            (d.Car_Name + '').toLowerCase().includes(searchCarNoOrName.trim().toLowerCase())
        );
    }
    const totalPages = Math.max(1, Math.ceil(filteredDetail.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = filteredDetail.length === 0 ? 0 : (safeCurrentPage - 1) * itemsPerPage + 1;
    const endIndex = filteredDetail.length === 0
        ? 0
        : Math.min(safeCurrentPage * itemsPerPage, filteredDetail.length);
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return (
        <>
            <Breadcrumbs page={name} items={[
                { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
                { name: name, url: '/deliveryCarInfo' },
            ]} />

            {/* Loading Spinner */}
            {isLoading && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex justify-content-center align-items-center"
                    style={{ zIndex: 9999 }}
                >
                    <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            <div className="row mt-1 hidden-scrollbar" style={{ overflowY: "auto" }}>
                <div className="col-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">

                                <div className="row mb-2">
                                    <div className="col-8">
                                        <input
                                            type="text"
                                            className="form-control input-spacing"
                                            placeholder="ค้นหาสายส่ง"
                                        />
                                    </div>
                                    <div className="col-4 d-flex justify-content-end">
                                        <button
                                            className="btn d-flex align-items-center text-white"
                                            style={{
                                                backgroundColor: 'rgb(239, 108, 0)',
                                                fontSize: '16px',
                                            }}
                                            onClick={() => {
                                                setShowModal(true);
                                                setMode('I');
                                            }}
                                        >
                                            <i className="fa fa-plus me-2" aria-hidden="true"></i>
                                            สร้างสายส่ง
                                        </button>
                                    </div>
                                </div>
                                <div className="table-responsive mt-3" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    <table id="basic-datatables" className="table table-striped table-hover">
                                        <thead className="thead-dark">
                                            <tr>
                                                <th className="text-center" style={{ width: '1%' }}>#</th>
                                                <th className="text-center" style={{ width: '17%' }}>ชื่อสายส่ง</th>
                                                <th className="text-center" style={{ width: '2%' }}>แก้ไข</th>
                                                <th className="text-center" style={{ width: '2%' }}>ลบ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dlLineList.length > 0 ? (
                                                dlLineList.map((dlLine, index) => (
                                                    <tr
                                                        key={dlLine.DL_Id}
                                                        onClick={async () => {
                                                            setDlLineId(dlLine.DL_Id);
                                                            setDlLineName(dlLine.DL_LineName);
                                                            await onRowSelected(dlLine.DL_Id);
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: dlLineId === dlLine.DL_Id ? 'rgb(105, 188, 216)' : 'transparent',
                                                            transition: 'background-color 0.3s ease'
                                                        }}
                                                    >
                                                        <td className="text-center">{index + 1}</td>
                                                        <td className="text-left">{dlLine.DL_LineName}</td>
                                                        <td className="text-center">
                                                            <i
                                                                className="fa fa-pen"
                                                                style={{ color: '#EF6C00', cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDlLineId(dlLine.DL_Id);
                                                                    setNewLineName(dlLine.DL_LineName);
                                                                    setShowModal(true);
                                                                    setMode('U');
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <i
                                                                className="fa fa-trash"
                                                                style={{
                                                                    color: 'red',
                                                                    cursor: 'not-allowed',
                                                                    opacity: 0.4,
                                                                }}
                                                            // onClick={(e) => {
                                                            //     e.stopPropagation();
                                                            //     setDlLineId(dlLine.DL_Id);
                                                            //     setShowConfirmModal(true);
                                                            // }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={8}>
                                                        <center>
                                                            <h5>ไม่พบข้อมูล</h5>
                                                        </center>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-8">
                    <CarQueueEditor
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        setIsLoading={setIsLoading}
                        dlLineName={dlLineName}
                        dlLineId={dlLineId}
                        dlLineDetail={dlLineDetail}
                        setDlLineDetail={setDlLineDetail}
                        searchCarNoOrName={searchCarNoOrName}
                        setSearchCarNoOrName={setSearchCarNoOrName}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        filteredDetail={filteredDetail}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        safeCurrentPage={safeCurrentPage}
                        Pagination={Pagination}
                        handlePageChange={handlePageChange}
                        carDataList={carDataList}
                        onRowSelected={() => onRowSelected(dlLineId)}
                    />
                </div>
            </div>
            {showModal && (
                <div className="modal show" tabIndex="-1" aria-labelledby="exampleModalLabel" style={{
                    display: 'block',
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="exampleModalLabel">เพิ่มสายส่งใหม่</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowModal(false);
                                        setDlLineName('');
                                    }}
                                />
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="lineName">ชื่อสายส่ง</label>
                                    <input
                                        type="text"
                                        id="lineName"
                                        className="form-control"
                                        value={newLineName}
                                        onChange={(e) => setNewLineName(e.target.value)}
                                        placeholder="กรอกชื่อสายส่ง"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-lg w-25 shadow text-white d-flex align-items-center justify-content-center"
                                    style={{
                                        backgroundColor: "#EF6C00",
                                        fontSize: "16px",
                                        position: "relative",
                                        whiteSpace: "nowrap",
                                    }}
                                    onClick={() => {
                                        mode === "I" ? confirmAndSubmit() : confirmAndUpdate();
                                    }}
                                >
                                    ยืนยัน
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-lg w-30 shadow text-white"
                                    style={{ backgroundColor: "red", fontSize: "16px" }}
                                    onClick={() => {
                                        setShowModal(false);
                                        setDlLineName('');
                                    }}
                                >
                                    ยกเลิก
                                </button>
                            </div>
                        </div>
                    </div>
                </div >
            )}
            {showConfirmModal && (
                <div className="modal show" tabIndex="-1" aria-labelledby="exampleModalLabel" style={{
                    display: 'block',
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="exampleModalLabel">ยืนยันการลบ</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                    }}
                                />
                            </div>
                            <div className="modal-body">
                                <p>คุณต้องการลบข้อมูลนี้จริงหรือไม่?</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-lg w-25 shadow text-white d-flex align-items-center justify-content-center"
                                    style={{
                                        backgroundColor: "#EF6C00",
                                        fontSize: "16px",
                                        position: "relative",
                                        whiteSpace: "nowrap",
                                    }}
                                    onClick={async () => {
                                        await handleDelete();
                                        setShowConfirmModal(false);
                                        setDlLineId('');
                                    }}
                                >
                                    ยืนยัน
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-lg w-30 shadow text-white"
                                    style={{ backgroundColor: "red", fontSize: "16px" }}
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        setDlLineId('');
                                    }}
                                >
                                    ยกเลิก
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Form; 